import React, { useMemo, useState } from "react";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../state";
import { isEqual } from "lodash";
import { getPreferredFormattedTimeMs } from "../../../utils/preferences";
import { OneParticipantInfo } from "../PitBoostCorrectedLeaderboard/types";
import { getColourMap } from "../../../utils";
import { numericalString } from "../../../types";
import { MdLoop } from "react-icons/md";

// Load showLoops preference from localStorage
const showLoopsSave = localStorage.getItem("showLoops");
const showLoopsDefault = showLoopsSave ? showLoopsSave === "true" : false;

export default function PitWindowPaceTable() {
  const [showLoops, setShowLoops] = useState(showLoopsDefault);

  const handleShowLoopsChange = (checked: boolean) => {
    setShowLoops(checked);
    localStorage.setItem("showLoops", String(checked));
  };

  const timingLoops = useSelector(
    (state: RootState) =>
      state.aks.v2.timing?.session?.track?.info?.timingLoops,
    isEqual,
  );

  const virtualGapsParticipants = useSelector(
    (state: RootState) => state.virtualGaps.data.participants,
    isEqual,
  );

  const sortedVirtualGapsParticipants = useMemo(() => {
    return virtualGapsParticipants
      ? (Object.values(virtualGapsParticipants) as OneParticipantInfo[]).sort(
          (a, b) => (a.virtualPosition ?? 0) - (b.virtualPosition ?? 0),
        )
      : [];
  }, [virtualGapsParticipants]);

  const hilightedParticipant = useSelector(
    (state: RootState) => state.virtualGaps.hilightedParticipant,
  );

  const participantDetails = useSelector(
    (state: RootState) =>
      state.aks.v2.timing?.session?.standings?.overall?.participantDetails,
    isEqual,
  );

  const entry = useSelector(
    (state: RootState) => state.aks.v2.timing?.session?.entry,
    isEqual,
  );

  const driverInfo = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.driverInfo,
    isEqual,
  );

  const pitInData = useSelector(
    (state: RootState) => state.aks.v2.timing?.analysis?.pitIn,
    isEqual,
  );

  const pitOutData = useSelector(
    (state: RootState) => state.aks.v2.timing?.analysis?.pitOut,
    isEqual,
  );

  // Map of loop keys to their display names
  const loopMap = useMemo(() => {
    if (!timingLoops) return {};
    const map: Record<string, string> = {};
    const keys = Object.keys(timingLoops).map(Number);
    const maxKey = Math.max(...keys);
    for (const [originalKey, loop] of Object.entries(timingLoops)) {
      let key = originalKey;
      // Remap loop 0 (FL) to last position
      if (key === "0") {
        key = (maxKey + 1).toString();
      }
      if (loop.name) {
        map[key] = loop.name;
      }
    }
    return map;
  }, [timingLoops]);

  // Get pit in lap numbers for a participant
  const getPitInLaps = (participant: string) => {
    const pits = pitInData?.[participant as numericalString]?.pits;
    if (!pits) return new Set();
    return new Set(
      Object.values(pits)
        .map((p) => p?.lapNumber)
        .filter((lap): lap is number => lap !== undefined),
    );
  };

  // Get pit out lap numbers for a participant
  const getPitOutLaps = (participant: string) => {
    const pits = pitOutData?.[participant as numericalString]?.pits;
    if (!pits) return new Set();
    return new Set(
      Object.values(pits)
        .map((p) => p?.lapNumber)
        .filter((lap): lap is number => lap !== undefined),
    );
  };

  // Get current loop and lap in timing.analysis.laps format
  const getCurrentLapLoop = (participant: string) => {
    const participantData =
      participantDetails?.[participant as numericalString];
    let currentLoop = participantData?.currentLoop;
    let currentLap = participantData?.currentLap;

    // When at FL (loop 0), treat as end of previous lap
    if (currentLoop === 0 && currentLap !== undefined && currentLap !== null) {
      currentLoop = Object.keys(loopMap).length;
      currentLap -= 1;
    }

    return { currentLoop, currentLap };
  };

  const getPerLoopTimesForLapAndLastLap = (participant: string) => {
    const participantData =
      participantDetails?.[participant as numericalString];
    const currentLoopSectors = participantData?.loopSectors;
    const lastLapLoopSectors = participantData?.lastLap?.loopSectors;

    const currentPerLoopTimes: Record<string, number> = {};
    let prevCumulativeTime = 0;
    Object.keys(loopMap).forEach((loopIdx) => {
      const cumulativeTime =
        currentLoopSectors?.[loopIdx as numericalString]?.time;
      if (cumulativeTime !== undefined) {
        currentPerLoopTimes[loopIdx] = cumulativeTime - prevCumulativeTime;
        prevCumulativeTime = cumulativeTime;
      }
    });

    const lastLapPerLoopTimes: Record<string, number> = {};
    prevCumulativeTime = 0;
    Object.keys(loopMap).forEach((loopIdx) => {
      const cumulativeTime =
        lastLapLoopSectors?.[loopIdx as numericalString]?.time;
      if (cumulativeTime !== undefined) {
        lastLapPerLoopTimes[loopIdx] = cumulativeTime - prevCumulativeTime;
        prevCumulativeTime = cumulativeTime;
      }
    });

    return { currentPerLoopTimes, lastLapPerLoopTimes };
  };

  const getCurrentLoopTimes = (participant: string) => {
    const { currentLap, currentLoop } = getCurrentLapLoop(participant);
    const { currentPerLoopTimes, lastLapPerLoopTimes } =
      getPerLoopTimesForLapAndLastLap(participant);
    const numLoops = Object.keys(loopMap).length;
    const perLoopTimes: Record<string, number> = {};
    const inLaps = getPitInLaps(participant);
    const outLaps = getPitOutLaps(participant);
    const lastLap = currentLap ? currentLap - 1 : 0;

    for (const loopIdx of Object.keys(loopMap)) {
      const loopNum = +loopIdx;

      if (currentLoop === numLoops) {
        // At FL - use all last lap times
        if (lastLapPerLoopTimes[loopIdx] !== undefined) {
          if (inLaps.has(currentLap ?? 0) && loopNum === numLoops) {
            perLoopTimes[loopIdx] = -1; // Indicate pit in for FL
          } else if (outLaps.has(currentLap ?? 0) && loopNum === 1) {
            perLoopTimes[loopIdx] = -2; // Indicate pit out for SCL1
          } else {
            perLoopTimes[loopIdx] = lastLapPerLoopTimes[loopIdx];
          }
        }
      } else if (currentPerLoopTimes[loopIdx] !== undefined) {
        // Have current lap time for this loop
        if (outLaps.has(currentLap ?? 0) && loopNum === 1) {
          perLoopTimes[loopIdx] = -2; // Indicate pit out for SCL1
        } else {
          perLoopTimes[loopIdx] = currentPerLoopTimes[loopIdx];
        }
      } else if (
        lastLapPerLoopTimes[loopIdx] !== undefined &&
        currentLoop !== undefined &&
        loopNum >= currentLoop + 2
      ) {
        // Loop is 2+ ahead of current - use last lap time
        if (inLaps.has(lastLap ?? 0) && loopNum === numLoops) {
          perLoopTimes[loopIdx] = -1; // Indicate pit in for FL
        } else {
          perLoopTimes[loopIdx] = lastLapPerLoopTimes[loopIdx];
        }
      }
    }

    return perLoopTimes;
  };

  // Get highlighted driver's loop times as reference
  const referenceLoopTimes = useMemo(() => {
    if (!hilightedParticipant) return {};
    return getCurrentLoopTimes(hilightedParticipant);
  }, [hilightedParticipant, participantDetails]);

  // Build record of reference driver's gap history for delta calculations
  const referenceGapHistory = useMemo(() => {
    if (!hilightedParticipant) return {};
    const vgParticipant =
      virtualGapsParticipants?.[hilightedParticipant as numericalString];
    if (!vgParticipant) return {};
    const history = vgParticipant.gapHistoryPitWindow;
    if (!history) return {};
    const gapRecord: Record<number, Record<number, number>> = {};
    for (const entry of history) {
      gapRecord[entry.lap] ??= {};
      if (
        entry.loop !== undefined &&
        entry.gapToRefPitCorrected !== undefined
      ) {
        gapRecord[entry.lap][entry.loop] ??= entry.gapToRefPitCorrected;
      }
    }
    return gapRecord;
  }, [hilightedParticipant, virtualGapsParticipants]);

  return (
    <div className={styles.container}>
      <div className={styles.leaderboardTable}>
        <table>
          <colgroup>
            <col className={styles.fixedCell} />
            <col className={styles.loopCell} />
            <col className={styles.loopCell} />
            <col className={styles.loopCell} />
            {showLoops && (
              <>
                <col className={styles.spacerCell} />
                {Object.values(loopMap).map((_, idx) => (
                  <col key={idx} className={styles.loopCell} />
                ))}
              </>
            )}
          </colgroup>
          <thead>
            <tr className={styles.titleRow}>
              <th colSpan={showLoops ? 5 + Object.values(loopMap).length : 4}>
                {showLoops ? "Opposite Strategy Pace" : "Opposite Pace"}
              </th>
            </tr>
            <tr>
              <th>Drv</th>
              <th>PW Δ</th>
              <th>LT Δ</th>
              <th>
                <MdLoop /> LT
              </th>
              {showLoops && (
                <>
                  <th className={styles.spacerCell}></th>
                  {Object.values(loopMap).map((name, idx) => (
                    <th key={idx}>{name}</th>
                  ))}
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {sortedVirtualGapsParticipants.map((vgParticipant, idx) => {
              if (!vgParticipant) return null;
              const participant = vgParticipant.participant;
              const driverInfoEntry =
                driverInfo?.[participant as numericalString];
              const entryData = entry?.[participant as numericalString];
              const driverName =
                entryData?.drivers?.["1"]?.shortName || participant;

              const isReference = participant === hilightedParticipant;

              // Determine if this driver is on same strategy as highlighted driver
              const referenceHasPitted = hilightedParticipant
                ? virtualGapsParticipants?.[
                    hilightedParticipant as numericalString
                  ]?.hasPitBoosted || false
                : false;
              const driverHasPitted = vgParticipant.hasPitBoosted || false;
              const isSameStrategy =
                hilightedParticipant &&
                !isReference &&
                referenceHasPitted === driverHasPitted;

              const rowStyle = isReference
                ? {}
                : {
                    color: driverInfoEntry?.driverFontColour || "white",
                    backgroundColor:
                      driverInfoEntry?.driverTeamColour || "#333",
                    opacity: isSameStrategy ? 0.3 : 1,
                  };

              const nameStyle = {
                color: driverInfoEntry?.driverFontColour || "white",
                backgroundColor: driverInfoEntry?.driverTeamColour || "#333",
                opacity: isSameStrategy ? 0.3 : 1,
              };

              const perLoopTimes = getCurrentLoopTimes(participant);

              // Calculate PW delta
              const driverHistory = vgParticipant.gapHistoryPitWindow;
              let pwDeltaDisplay = "-";
              let pwDeltaStyle = {
                backgroundColor: "#fff",
                color: "#000",
              };

              if (
                hilightedParticipant &&
                !isReference &&
                driverHistory &&
                driverHistory.length >= 2
              ) {
                // Find first and last entries where both driver and reference have data
                let firstDriverEntry = null;
                let firstRefGap: number | null = null;
                let lastDriverEntry = null;
                let lastRefGap: number | null = null;
                for (const entry of driverHistory) {
                  const refGap = referenceGapHistory[entry.lap]?.[entry.loop];
                  if (refGap !== undefined) {
                    if (firstDriverEntry === null) {
                      firstDriverEntry = entry;
                      firstRefGap = refGap;
                    }
                    lastDriverEntry = entry;
                    lastRefGap = refGap;
                  }
                }
                if (
                  firstDriverEntry &&
                  lastDriverEntry &&
                  firstRefGap !== null &&
                  lastRefGap !== null
                ) {
                  const deltaStart =
                    firstDriverEntry.gapToRefPitCorrected - firstRefGap;
                  const deltaEnd =
                    lastDriverEntry.gapToRefPitCorrected - lastRefGap;
                  const pwDelta = deltaEnd - deltaStart;
                  const sign = pwDelta > 0 ? "+" : "";
                  pwDeltaDisplay = `${sign}${pwDelta.toFixed(1)}`;
                  pwDeltaStyle = {
                    backgroundColor: getColourMap(pwDelta, 3, -3),
                    color: "#000",
                  };
                }
              }

              // Calculate the rolling lap time delta from virtual gaps data
              let ltDeltaDisplay = "-";
              let ltDeltaStyle = { backgroundColor: "#fff", color: "#000" };
              const driverRollingLT = vgParticipant.rollingLapTime;
              const refRollingLT = hilightedParticipant
                ? virtualGapsParticipants?.[
                    hilightedParticipant as numericalString
                  ]?.rollingLapTime
                : null;
              if (
                hilightedParticipant &&
                !isReference &&
                driverRollingLT &&
                refRollingLT
              ) {
                const ltDelta = driverRollingLT - refRollingLT;
                ltDeltaDisplay = `${ltDelta > 0 ? "+" : ""}${ltDelta.toFixed(
                  3,
                )}`;
                ltDeltaStyle = {
                  backgroundColor: getColourMap(ltDelta * 1000, 500, -500),
                  color: "#000",
                };
              }

              // Rolling lap time display
              const rollingLTDisplay = driverRollingLT
                ? driverRollingLT.toFixed(3)
                : "-";

              return (
                <tr key={participant ?? idx} style={rowStyle}>
                  <td style={nameStyle}>{driverName}</td>
                  <td style={pwDeltaStyle}>{pwDeltaDisplay}</td>
                  <td style={ltDeltaStyle}>{ltDeltaDisplay}</td>
                  <td style={{ backgroundColor: "#fff", color: "#000" }}>
                    {rollingLTDisplay}
                  </td>
                  {showLoops && (
                    <>
                      <td className={styles.spacerCell}></td>
                      {Object.keys(loopMap).map((loopIdx) => {
                        const loopTime = perLoopTimes[loopIdx];
                        const refTime = referenceLoopTimes[loopIdx];

                        // Check for pit in/out markers
                        const isPitIn = loopTime === -1;
                        const isPitOut = loopTime === -2;
                        const refIsPitIn = refTime === -1;
                        const refIsPitOut = refTime === -2;

                        const hasTime = loopTime !== undefined && loopTime > 0;
                        const hasRefTime = refTime !== undefined && refTime > 0;

                        // If reference is pitting, show raw times for others (no delta)
                        const refIsPitting = refIsPitIn || refIsPitOut;

                        const delta =
                          hasTime && hasRefTime && !refIsPitting
                            ? loopTime - refTime
                            : null;
                        const showDelta =
                          delta !== null &&
                          hilightedParticipant &&
                          !isReference;

                        let displayValue: string;
                        let cellStyle: {
                          backgroundColor: string;
                          color: string;
                        };

                        if (isReference && (refIsPitIn || refIsPitOut)) {
                          // Reference driver pit in/out
                          displayValue = refIsPitIn ? "IN" : "OUT";
                          cellStyle = {
                            backgroundColor: "lightcoral",
                            color: "#000",
                          };
                        } else if (!isReference && (isPitIn || isPitOut)) {
                          // Other driver pit in/out
                          displayValue = isPitIn ? "IN" : "OUT";
                          cellStyle = {
                            backgroundColor: "lightcoral",
                            color: "#000",
                          };
                        } else if (showDelta) {
                          displayValue = `${delta > 0 ? "+" : ""}${(
                            delta / 1000
                          ).toFixed(3)}`;
                          cellStyle = {
                            backgroundColor: getColourMap(delta, 200, -200),
                            color: "#000",
                          };
                        } else if (hasTime) {
                          displayValue = getPreferredFormattedTimeMs(loopTime);
                          cellStyle = {
                            backgroundColor: "#fff",
                            color: "#000",
                          };
                        } else {
                          displayValue = "-";
                          cellStyle = {
                            backgroundColor: "#fff",
                            color: "#000",
                          };
                        }

                        return (
                          <td key={loopIdx} style={cellStyle}>
                            {displayValue}
                          </td>
                        );
                      })}
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className={styles.controls}>
        <label className={styles.radioOption}>
          <input
            type="checkbox"
            checked={showLoops}
            onChange={(e) => handleShowLoopsChange(e.target.checked)}
          />
          Show Loops
        </label>
      </div>
    </div>
  );
}
